/**
 * DealItemsList - Lista de produtos/serviços do Deal
 * Permite adicionar, editar e remover itens com cálculo automático
 * Versão: 1.0.0
 */
import React, { useState } from 'react';
import { Plus, Trash2, Package } from 'lucide-react';
import { useCrmDealItems } from '@/hooks/useCrmDealItems';
import { useCrmProducts } from '@/hooks/useCrmProducts';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { PriceDisplay } from '@/components/crm/PriceDisplay';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

// Props do componente
interface DealItemsListProps {
  dealId: string;
  currency?: string;
  readOnly?: boolean;
}

/**
 * Componente para gerenciar itens do Deal
 */
export function DealItemsList({ 
  dealId, 
  currency = 'BRL',
  readOnly = false 
}: DealItemsListProps) {
  const { currentTenant } = useAuth();
  const { 
    items, 
    isLoading, 
    addItem, 
    updateItem, 
    removeItem,
    totals 
  } = useCrmDealItems(dealId);
  
  const { products } = useCrmProducts();
  
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [quantity, setQuantity] = useState<number>(1);

  // Adiciona produto ao deal
  const handleAddItem = async () => {
    if (!selectedProductId || !dealId) return;
    
    const product = products.find(p => p.id === selectedProductId);
    if (!product) return;

    await addItem.mutateAsync({
      deal_id: dealId,
      product_id: selectedProductId,
      quantity: quantity,
      unit_price: product.default_price,
    });

    setSelectedProductId('');
    setQuantity(1);
  };

  // Atualiza quantidade do item
  const handleUpdateQuantity = async (itemId: string, newQuantity: number) => {
    if (newQuantity < 1) return;
    await updateItem.mutateAsync({
      id: itemId,
      quantity: newQuantity,
    });
  };

  // Atualiza desconto percentual
  const handleUpdateDiscount = async (itemId: string, discountPercent: number) => {
    await updateItem.mutateAsync({
      id: itemId,
      discount_percent: discountPercent,
    });
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[1, 2].map(i => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Package className="h-4 w-4" />
          Produtos/Serviços
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Adicionar novo item */}
        {!readOnly && (
          <div className="flex gap-2">
            <Select value={selectedProductId} onValueChange={setSelectedProductId}>
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Selecione um produto..." />
              </SelectTrigger>
              <SelectContent>
                {products.map(product => (
                  <SelectItem key={product.id} value={product.id}>
                    {product.name} - {new Intl.NumberFormat('pt-BR', { 
                      style: 'currency', 
                      currency 
                    }).format(product.default_price)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="number"
              min={1}
              value={quantity}
              onChange={(e) => setQuantity(Number(e.target.value))}
              className="w-20"
            />
            <Button 
              onClick={handleAddItem}
              disabled={!selectedProductId || addItem.isPending}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Lista de itens */}
        {items.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>Nenhum produto adicionado.</p>
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Produto</TableHead>
                  <TableHead className="w-24 text-center">Qtd</TableHead>
                  <TableHead className="w-28 text-right">Preço Unit.</TableHead>
                  <TableHead className="w-24 text-center">Desc. %</TableHead>
                  <TableHead className="w-28 text-right">Total</TableHead>
                  {!readOnly && <TableHead className="w-12" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map(item => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{item.product?.name}</p>
                        {item.product?.sku && (
                          <p className="text-xs text-muted-foreground">
                            SKU: {item.product.sku}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {readOnly ? (
                        item.quantity
                      ) : (
                        <Input
                          type="number"
                          min={1}
                          value={item.quantity}
                          onChange={(e) => handleUpdateQuantity(item.id, Number(e.target.value))}
                          className="w-16 text-center mx-auto"
                        />
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <PriceDisplay amount={item.unit_price} currency={currency} size="sm" />
                    </TableCell>
                    <TableCell className="text-center">
                      {readOnly ? (
                        `${item.discount_percent || 0}%`
                      ) : (
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          value={item.discount_percent || 0}
                          onChange={(e) => handleUpdateDiscount(item.id, Number(e.target.value))}
                          className="w-16 text-center mx-auto"
                        />
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      <PriceDisplay amount={item.total} currency={currency} size="sm" />
                    </TableCell>
                    {!readOnly && (
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => removeItem.mutate(item.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Totalizadores */}
            <div className="border-t pt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <PriceDisplay amount={totals.subtotal} currency={currency} />
              </div>
              {totals.totalDiscounts > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Descontos</span>
                  <span>- <PriceDisplay amount={totals.totalDiscounts} currency={currency} /></span>
                </div>
              )}
              <div className="flex justify-between font-semibold text-lg border-t pt-2">
                <span>Total</span>
                <PriceDisplay amount={totals.total} currency={currency} />
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
